import { useState, useEffect } from 'react';
import '../styles/AppUpdate.css';

// Use production URL if it looks like we are in production, otherwise fallback to localhost
const PRODUCTION_URL = 'https://flyhub-webadmin-127883848645.asia-south1.run.app';
const LOCAL_URL = 'http://localhost:5002';

export default function AppUpdateManagement() {
    // Always default to production API — toggle to local only when explicitly needed
    const [useProductionApi, setUseProductionApi] = useState(true);
    const API_BASE_URL = useProductionApi ? PRODUCTION_URL : LOCAL_URL;
    
    const [version, setVersion] = useState('');
    const [forceUpdate, setForceUpdate] = useState(false);
    const [apkFile, setApkFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentConfig, setCurrentConfig] = useState(null);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchCurrentConfig();
    }, [useProductionApi]);

    const fetchCurrentConfig = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/update.json`);
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            setCurrentConfig(data);
            setVersion(data.version);
            setForceUpdate(data.force_update);
        } catch (err) {
            console.error('Failed to fetch config:', err);
            setCurrentConfig(null);
            if (!useProductionApi && window.location.hostname === 'localhost') {
                setMessage('Warning: Local backend not reachable. You might want to switch to Production API.');
            }
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!apkFile || !version) {
            alert('Please provide both a version and an APK file');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const CHUNK_SIZE = 5 * 1024 * 1024;
            const totalChunks = Math.ceil(apkFile.size / CHUNK_SIZE);
            const uploadId = `upload_${Date.now()}`;

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, apkFile.size);
                const chunk = apkFile.slice(start, end);

                setMessage(`Uploading piece ${i + 1} of ${totalChunks} (${((i/totalChunks)*100).toFixed(0)}%)...`);
                
              const formData = new FormData();
formData.append('chunk', chunk);  // IMPORTANT: name must be 'chunk'

const response = await fetch(`${API_BASE_URL}/upload-chunk`, {
    method: 'POST',
    headers: {
        'x-chunk-index': i.toString(),
        'x-upload-id': uploadId,
    },
    body: formData
});

                if (!response.ok) throw new Error(`Upload failed at piece ${i + 1}`);
            }

            setMessage('Almost done... assembling the pieces on the server...');
            const finalizeRes = await fetch(`${API_BASE_URL}/finalize-upload`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    uploadId,
                    totalChunks,
                    version,
                    forceUpdate
                })
            });

            const finalizeData = await finalizeRes.json();

            if (finalizeData.success) {
                setMessage('Update successfully published! (All pieces combined)');
                setApkFile(null);
                fetchCurrentConfig();
            } else {
                setMessage('Error assembling APK: ' + (finalizeData.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Hand-off failed:', err);
            setMessage('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete the active app version and APK? This cannot be undone.')) {
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await fetch(`${API_BASE_URL}/delete-config`, {
                method: 'DELETE',
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Configuration and APK deleted successfully');
                setCurrentConfig(null);
                setVersion('');
                setForceUpdate(false);
            } else {
                setMessage('Error: ' + (data.error || 'Failed to delete configuration'));
            }
        } catch (err) {
            console.error('Delete failed:', err);
            setMessage('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-update-container">
            <div className="app-update-card">
                <div className="app-update-header">
                    <h1>App Auto-Update Management</h1>
                    <p>Control the version and APK file served to mobile users</p>
                    
                    {window.location.hostname === 'localhost' && (
                        <div className="api-toggle-container">
                            <label className="api-toggle">
                                <input 
                                    type="checkbox" 
                                    checked={useProductionApi} 
                                    onChange={(e) => {
                                        setUseProductionApi(e.target.checked);
                                        setMessage('');
                                    }} 
                                />
                                <span className="toggle-slider"></span>
                                <span className="toggle-label">
                                    {useProductionApi ? 'Using Production API' : 'Using Local API (Port 5002)'}
                                </span>
                            </label>
                        </div>
                    )}
                </div>

                <div className="app-update-content">
                    {/* Current Config Section */}
                    <div className="current-config-section">
                        <h2>Currently Active</h2>
                        {currentConfig ? (
                            <div className="config-card">
                                <div className="config-item">
                                    <span className="config-label">Version</span>
                                    <span className="config-version-badge">{currentConfig.version}</span>
                                </div>
                                <div className="config-item">
                                    <span className="config-label">Force Update</span>
                                    <span className={`force-update-badge ${currentConfig.force_update ? 'enabled' : 'disabled'}`}>
                                        {currentConfig.force_update ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                </div>
                                <div className="config-apk-url">
                                    APK URL: {currentConfig.apk_url}
                                </div>
                            </div>
                        ) : (
                            <div className="loading-skeleton">
                                <div className="skeleton-line"></div>
                                <div className="skeleton-line"></div>
                            </div>
                        )}
                        {currentConfig && (
                            <button 
                                onClick={handleDelete} 
                                disabled={loading}
                                className="delete-config-button"
                            >
                                {loading ? 'Deleting...' : 'Delete Active Version'}
                            </button>
                        )}
                    </div>

                    {/* Form Section */}
                    <form onSubmit={handleUpload} className="update-form">
                        <div className="form-group">
                            <label>New Version Number</label>
                            <input 
                                type="text" 
                                placeholder="e.g. 1.0.1" 
                                value={version}
                                onChange={(e) => setVersion(e.target.value)}
                                required
                            />
                        </div>

                        <div className="checkbox-group">
                            <input 
                                type="checkbox" 
                                id="forceUpdate"
                                checked={forceUpdate}
                                onChange={(e) => setForceUpdate(e.target.checked)}
                            />
                            <label htmlFor="forceUpdate">Force users to update</label>
                        </div>

                        <div className="form-group">
                            <label>APK File</label>
                            <div className="file-upload-area">
                                <input 
                                    type="file" 
                                    accept=".apk"
                                    id="apk-upload"
                                    onChange={(e) => setApkFile(e.target.files[0])}
                                />
                                <label htmlFor="apk-upload" className="file-upload-label">
                                    {apkFile ? (
                                        <div className="file-selected">
                                            <svg className="success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <p className="file-name">{apkFile.name}</p>
                                            <p className="file-size">{(apkFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                                        </div>
                                    ) : (
                                        <div className="file-placeholder">
                                            <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            <p className="upload-text">Click to select APK</p>
                                            <p className="upload-hint">Drag and drop also works</p>
                                        </div>
                                    )}
                                </label>
                            </div>
                        </div>

                        {message && (
                            <div className={`message ${message.startsWith('Error') ? 'message-error' : 'message-success'}`}>
                                {message}
                            </div>
                        )}

                        <button 
                            type="submit" 
                            disabled={loading || !apkFile}
                            className="submit-button"
                        >
                            {loading ? (
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                    Uploading...
                                </div>
                            ) : 'Publish Update'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}